/**
 * digital-twins.data.ts — Content for the "Digital Twins" chapter (CX-2729).
 *
 * Imagio® CQ digital material twins (AxF), Autodesk VRED integration, and the
 * "Same geometry, different material" interactive comparison. Ported from the
 * demo-v784 reference; static today, CMS-driven later (same `ChapterContent`
 * shape). Uses ordered `blocks` wrapped in bands (CX-2725 §11 pattern): white
 * (intro + AxF/VRED callouts + impact), gray (image carousel), light (samples).
 *
 * Figma > AC note: the Figma "MaterialSampleCardRow" pairs two materials per
 * product-render image (4 rows of 2). We flatten this to 8 individual
 * `MaterialSamplesList` entries (reusing the shared component from 2726/2727)
 * since each entry already carries the full Color/Material/Opacity/Surface
 * Texture spec the AC asks for — no new paired-row component needed for this
 * chapter (see CMF library handoff doc §5).
 */

import type { ChapterContent } from "../chapter.types";

const DIGITAL_TWINS_HERO_IMAGE =
  "https://mediahub.covestro.com/transform/15ea0eba-f2e2-4d39-befa-c3c8f8cedbc8/IMAGIO-from-Sketch-to-Virtual-Prototype";

const CAROUSEL_ITEM_1 =
  "https://mediahub.covestro.com/transform/95df826b-c14e-450d-a14c-cd24c0739745/Covestro-MMI-25MOB11-12-Sparkling-Intelligence-Black-Crystal";
const CAROUSEL_ITEM_2 =
  "https://mediahub.covestro.com/transform/2fd91f4f-b3be-413e-8430-f8352ab576ad/Covestro-MMI-25MOB41-42-Dark-Matter-Bright-Lime";
const CAROUSEL_ITEM_3 =
  "https://mediahub.covestro.com/transform/74a7934c-2299-4d29-913d-f73799eb63c6/Covestro-MMI-25MOB32-32-Levitation-Gravitation-Orange";
const CAROUSEL_ITEM_4 =
  "https://mediahub.covestro.com/transform/6ae0cb32-96d3-4e04-bcee-18f272d432b0/Covestro-MMI-25MOB42-22-Grit-Horizon";

const CHIP_1 =
  "https://mediahub.covestro.com/transform/ae0fddd5-7e4f-4298-9661-6f7eb5c2e115/2025-CMF-Trends-Aesthetic-Toolkits_MOB1";
const CHIP_2 =
  "https://mediahub.covestro.com/transform/0cd5473c-54c6-472f-943a-ec4109ee70c6/2025-CMF-Trends-Aesthetic-Toolkits_MOB4";
const CHIP_3 =
  "https://mediahub.covestro.com/transform/d2d0278f-4d8d-446b-a19f-6ef00238b918/2025-CMF-Trends-Aesthetic-Toolkits_MOB3";
const CHIP_4 =
  "https://mediahub.covestro.com/transform/2a708b87-9798-47ce-9e8e-3ad1f1930960/2025-CMF-Trends-Aesthetic-Toolkits_MOB2";

export const digitalTwinsChapter: ChapterContent = {
  slug: "digital-twins",
  title: "Digital Twins",
  eyebrow: "Sustainability Guide",
  heroImage: DIGITAL_TWINS_HERO_IMAGE,
  tags: ["Digital Sampling", "Imagio CQ", "Dematerialization", "AxF", "VRED"],
  intro:
    'Physical material sampling has a hidden environmental cost. A single chip can require up to 25 kg of material to produce — most of which ends up as waste from cleaning the production equipment between runs. Add express shipping across continents and multiple rounds of iteration, and the carbon footprint of the design phase itself becomes significant.\n\nDematerialization means replacing physical samples with digital equivalents wherever possible — reducing waste, transport and time without compromising design quality. This is not about eliminating physical evaluation entirely. It is about making every physical sample count by front-loading decisions digitally. By shifting material evaluation to the digital phase, designers can explore a wider range of options in less time — and only request physical samples for final validation. This reduces sampling rounds from an average of 3–5 to 1–2, cutting material waste and shipping emissions proportionally.\n\nThe suffix "CQ" stands for Circular Intelligence. It signals that the technology makes its contribution to the circular economy through material savings — not just through the material\'s origin. Imagio CQ is the program under which Covestro offers all digital sampling services. It is designed to get involved at the earliest stage of product design, when the first material specifications are being defined — and to enable collaboration between cross-site teams who need to share and evaluate material options without shipping physical parts.',

  // Full-width alternating bands (CX-2725 §11 pattern): white holds the
  // Imagio CQ / AxF explainer + Impact; gray holds the "same geometry"
  // carousel; light holds the Material Samples list (matches the Figma
  // section rhythm — nodes 1659-12746 white / 1659-12871 #f9f9f9 / 1659-12901
  // #f3f3f3, the last close enough to the shared "gray"/"light" band tones).
  blocks: [
    {
      kind: "band",
      surface: "white",
      blocks: [
        { kind: "subheading", text: "Imagio® CQ — Digital Material Twins" },
        {
          kind: "paragraph",
          text: "Imagio® CQ introduces digital twins of every material formula produced at Covestro's Global Color & Design Centers. Captured in AxF (Appearance eXchange File Format), each digital material encodes color, gloss, opacity, texture, refraction, transparency, translucency, and special effects — the full appearance signature of the physical material. Apply it directly to your CAD model in most major 3D software packages. Evaluate materials photorealistically before a single piece of resin is molded.",
        },
        {
          kind: "note",
          note: {
            tone: "info",
            icon: "🔗",
            text: "As of September 2025, Imagio is directly integrated into Autodesk VRED 2026.1 — the standard 3D visualization platform for automotive design. Covestro materials are available natively in your existing workflow, with no file import required. Vehicle designers can visualize material effects photorealistically without physical samples, directly in the tools they already use.",
          },
        },
        {
          kind: "note",
          note: {
            tone: "info",
            icon: "💡",
            text: "The Appearance eXchange File Format (AxF) is a vendor-neutral format developed by X-Rite. It stores color, texture, gloss, refraction, transparency, translucency, special effects, and reflection properties in a single editable file — comparable to a PDF for material appearance. AxF is supported by most major 3D and rendering software packages and can be applied directly to CAD drawings to create virtual prototypes with photorealistic accuracy.",
          },
        },
        { kind: "subheading", text: "Impact" },
        {
          kind: "paragraph",
          text: "By shifting material evaluation to the digital phase, designers can explore a wider range of options in less time — and only request physical samples for final validation. This reduces the number of sampling rounds from an average of 3–5 to 1–2, cutting material waste and shipping emissions proportionally.",
        },
      ],
    },
    {
      kind: "band",
      surface: "gray",
      blocks: [
        {
          kind: "imageCarousel",
          title: "Same geometry, different material",
          carousel: {
            items: [
              {
                image: CAROUSEL_ITEM_1,
                chip: CHIP_1,
                label: "Sparkling Intelligence — Black Crystal",
              },
              {
                image: CAROUSEL_ITEM_2,
                chip: CHIP_2,
                label: "Dark Matter — Bright Lime",
              },
              {
                image: CAROUSEL_ITEM_3,
                chip: CHIP_3,
                label: "Levitation — Gravitation Orange",
              },
              {
                image: CAROUSEL_ITEM_4,
                chip: CHIP_4,
                label: "Grit — Horizon",
              },
            ],
          },
        },
      ],
    },
    {
      kind: "band",
      surface: "light",
      blocks: [
        {
          kind: "samples",
          // TODO(CX-2729): demo reference has no real Imagio URL for these 5
          // samples (colorFinderUrl: '#') — "view-in-imagio" hides its CTA for
          // an invalid target (CX-2764 AC), so these use "check-it-now"
          // (in-app search) until the real Imagio link is available.
          samples: [
            {
              name: "Sparkling Intelligence",
              productImage: CHIP_1,
              colorHex: "#152838",
              material: "Makrolon® A2497",
              ralColor: "RAL 250 30 15",
              opacity: "Opaque, Pearl",
              surfaceTexture: "Gloss Front / Matt Back",
              link: { variant: "check-it-now" },
            },
            {
              name: "Black Crystal",
              productImage: CHIP_1,
              colorHex: "#2E3031",
              material: "Makrolon® A2417",
              ralColor: "RAL 000 25 00",
              opacity: "Transparent",
              surfaceTexture: "Gloss Back",
              link: { variant: "check-it-now" },
            },
            {
              name: "Grit",
              productImage: CHIP_4,
              colorHex: "#8B7660",
              material: "Makrolon® A2497 RE",
              ralColor: "RAL 060 50 20",
              opacity: "Opaque, Marble",
              surfaceTexture: "Matt Front / Gloss Back",
              link: { variant: "check-it-now" },
            },
            {
              name: "Horizon Line",
              productImage: CHIP_4,
              colorHex: "#93D1CE",
              material: "Bayblend® T85XUV",
              ralColor: "RAL 190 85 20",
              opacity: "Opaque",
              surfaceTexture: "Gloss Back",
              link: { variant: "check-it-now" },
            },
            {
              name: "Levitation",
              productImage: CHIP_3,
              colorHex: "#9D9D9D",
              material: "Makrolon® FR6005UV",
              ralColor: "RAL 000 80 00",
              opacity: "Opaque, Metallic",
              surfaceTexture: "Matt Front / Gloss Back",
              link: {
                variant: "view-in-imagio",
                imagioUrl:
                  "/search?preliminaryColorCode=24G195M&colorSystem=preliminary_color_code",
              },
            },
            {
              name: "Gravitation Orange",
              productImage: CHIP_3,
              colorHex: "#EE7541",
              material: "Bayblend® FR3010",
              ralColor: "RAL 050 60 70",
              opacity: "Opaque",
              surfaceTexture: "Matt Back",
              link: {
                variant: "view-in-imagio",
                imagioUrl:
                  "/search?finalColorCode=201351&colorSystem=covestro_color_code",
              },
            },
            {
              name: "Dark Matter",
              productImage: CHIP_2,
              colorHex: "#4D4E50",
              material: "Bayblend® T85X R25A",
              ralColor: "RAL 000 25 00",
              opacity: "Opaque",
              surfaceTexture: "Matt Front / Gloss Front",
              link: { variant: "check-it-now" },
            },
            {
              name: "Bright Lime",
              productImage: CHIP_2,
              colorHex: "#BED5AE",
              material: "Bayblend® T85X R25A",
              ralColor: "RAL 140 85 10",
              opacity: "Opaque",
              surfaceTexture: "Gloss Back",
              link: { variant: "check-it-now" },
            },
          ],
        },
      ],
    },
  ],
};
